import { UserWithRoles } from "@ac-expo/api-types";
import { useSyncedCart } from "@ac-expo/hooks";
import {
  Button,
  RedDot,
  Separator,
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@ac-expo/ui-components";
import OwnImage from "@ui/components/ui/event/OwnImage";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import { useMessageContext } from "@web-app/src/context/websocket/message.context";
import { useNotificationContext } from "@web-app/src/context/websocket/notifications.context";
import environment from "@web-app/src/environments/environment";
import GlobalSearch from "@web-app/src/shared/GlobalSearch";
import Link from "next/link";
import { useRouter } from "next/router";
import { FC, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { SvgWeb } from "../../../../public/images/svg/SvgWeb";
import {
  BagIcon,
  BellIcon,
  ChatCircleIcon,
  ClientsIcon,
  GearIcon,
  LifebuoyIcon,
  PresentationIcon,
  ShareIcon,
  SignOutIcon,
  TicketIcon,
  UserIcon,
} from "../../../icons";
import filterStore from "../../../services/FilterStore";
import AddEvent from "../../Event/AddEvent";
import { NO_REDIRECT_AT_DISCONNECT_ROUTES } from "../UserPopover";
import { AuthButtons } from "./AuthButtons";

type NavMenuMobileProps = {
  handleCartClick: () => void;
  user?: UserWithRoles;
};

export const NavMenuMobile = ({
  handleCartClick,
  user,
}: NavMenuMobileProps) => {
  const router = useRouter();
  const { haveNewMessage, markAsRead } = useMessageContext();
  const { notifications } = useNotificationContext();
  const { disconnect, isAuthenticated } = useUserContext();
  const [isOpen, setIsOpen] = useState(false);
  const pathname = router.asPath;

  const { hasItems } = useSyncedCart({ basketType: "store", isAuthenticated });
  const hasNotReadNotifications = !notifications?.pages?.[0]?.data?.[0]?.read;

  const AddEventButton: FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
      <Button onClick={onClick} variant="blue" className="font-semibold">
        Ajouter un événement
      </Button>
    );
  };

  const onDisconnect = () => {
    disconnect();
    if (!NO_REDIRECT_AT_DISCONNECT_ROUTES.includes(pathname)) {
      router.push("/");
    }
  };
  return (
    <nav className="flex w-full items-center border-b border-gray-200 bg-white py-4 dark:bg-gray-900 lg:hidden">
      <div className="flex w-full flex-col gap-2 px-[14px]">
        <div className="flex items-center justify-between">
          <Link href={"/"}>
            <button
              onClick={() => {
                filterStore.resetStore();
                filterStore.setShowMap(false);
              }}
            >
              <div className="flex h-[32px]">
                <OwnImage
                  src="/images/logos/logo_connect_events_horizontale.png"
                  alt="logo avec nom Connect'Events"
                  className="static h-9 w-auto p-1"
                  width={880}
                  height={200}
                />
              </div>
            </button>
          </Link>
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button className="border-none shadow-none">
                <SvgWeb
                  name="hamburger"
                  alt="hamburger"
                  width={24}
                  height={24}
                />
              </Button>
            </SheetTrigger>
            <SheetContent className="flex w-3/4 flex-col overflow-scroll bg-white px-[14px] py-1 sm:max-w-lg">
              <SheetHeader className="flex w-full items-center justify-between border-b-white px-[14px]">
                <div className="flex w-full justify-between gap-4">
                  <VisuallyHidden>
                    <SheetTitle>Menu de navigation</SheetTitle>
                  </VisuallyHidden>
                  <div className="flex w-full">
                    <OwnImage
                      src="/images/logos/logo_connect_events_horizontale.png"
                      alt="logo avec nom Connect'Events"
                      className="static h-auto w-full p-1"
                      width={880}
                      height={200}
                    />
                  </div>
                  <SheetClose className="w-fit shrink-0" asChild>
                    <button className="focus:outline-none">
                      <SvgWeb name="close" alt="close" width={24} height={24} />
                    </button>
                  </SheetClose>
                </div>
              </SheetHeader>
              <div className="flex grow flex-col gap-3">
                <div className="grid gap-5 px-1 py-4 text-sm font-medium text-gray-700">
                  {user && !user.isGuestCheckout ? (
                    <>
                      <button
                        className="flex items-center gap-3"
                        onClick={handleCartClick}
                      >
                        <div className="relative">
                          <BagIcon width={16} height={16} fill={"#287BB9"} />
                          {hasItems && (
                            <RedDot className="absolute right-0 top-0" />
                          )}
                        </div>
                        <p>Mon panier</p>
                      </button>
                      <Link
                        href={"/conversations"}
                        onClick={() => {
                          markAsRead();
                          setIsOpen(false);
                        }}
                      >
                        <div className=" flex items-center gap-3">
                          <div className="relative">
                            <ChatCircleIcon
                              width={16}
                              height={16}
                              fill={"#287BB9"}
                            />
                            {haveNewMessage && (
                              <RedDot className="absolute right-0 top-0" />
                            )}
                          </div>

                          <p>Messagerie</p>
                        </div>
                      </Link>
                      <Link
                        href={"/profile/notifications"}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <BellIcon width={16} height={16} fill={"#287BB9"} />
                            {hasNotReadNotifications && (
                              <RedDot className="absolute right-0 top-0" />
                            )}
                          </div>
                          <p>Notifications</p>
                        </div>
                      </Link>
                      <Separator />
                      <Link href={"/profile"} onClick={() => setIsOpen(false)}>
                        <div className="flex items-center gap-3">
                          <UserIcon width={16} height={16} fill={"#287BB9"} />
                          <p>Mon profil</p>
                        </div>
                      </Link>
                      <Link
                        href={"/profile/mes-billets"}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center gap-3">
                          <TicketIcon width={16} height={16} fill={"#287BB9"} />
                          <p>Mes billets</p>
                        </div>
                      </Link>
                      <Link
                        href={"/profile/my-exhibitors"}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center gap-3">
                          <PresentationIcon
                            width={16}
                            height={16}
                            fill={"#287BB9"}
                          />
                          <p>Mes exposants</p>
                        </div>
                      </Link>
                      <Link
                        href={"/profile/my-favorite-events"}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center gap-3">
                          <TicketIcon width={16} height={16} fill={"#287BB9"} />
                          <p>Mes événements favoris</p>
                        </div>
                      </Link>
                      {user?.isProfessional && (
                        <Link
                          href={"/profile/my-clients"}
                          onClick={() => setIsOpen(false)}
                        >
                          <div className="flex items-center gap-4">
                            <ClientsIcon
                              width={16}
                              height={16}
                              fill={"#287BB9"}
                            />
                            <p className="text-sm text-gray-700">Mes clients</p>
                          </div>
                        </Link>
                      )}
                      <Link
                        href={"/profile/my-settings"}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center gap-3">
                          <GearIcon width={16} height={16} fill={"#287BB9"} />
                          <p>Mes paramètres</p>
                        </div>
                      </Link>
                      <Link
                        href={"/profile/help"}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center gap-3">
                          <LifebuoyIcon
                            width={16}
                            height={16}
                            fill={"#287BB9"}
                          />
                          <p>Aide</p>
                        </div>
                      </Link>
                      <Separator />
                      <div className="flex w-full flex-col gap-3">
                        {user?.isProfessional && (
                          <>
                            <Link
                              href={environment.BACKOFFICE_URL}
                              target="_blank"
                              className="flex flex-col gap-3"
                              onClick={() => setIsOpen(false)}
                            >
                              <Button
                                className="flex items-center gap-4 font-semibold"
                                variant="outline-blue"
                              >
                                <ShareIcon
                                  width={16}
                                  height={16}
                                  fill={"#287BB9"}
                                />
                                <p className="text-sm text-gray-700">
                                  Accéder à mon back office
                                </p>
                              </Button>
                            </Link>
                          </>
                        )}
                        <AddEvent TriggerElement={AddEventButton} />
                      </div>
                      {user && !user.isGuestCheckout && (
                        <div className="flex items-center border-t p-4">
                          <button
                            onClick={onDisconnect}
                            className="flex items-center gap-2 font-semibold"
                          >
                            <SignOutIcon
                              width={16}
                              height={16}
                              fill={"#98A2B3"}
                            />
                            <p>Se déconnecter</p>
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className=" ">
                      <AuthButtons handleCartClick={handleCartClick} />
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <GlobalSearch />
      </div>
    </nav>
  );
};
